import React, { useState } from 'react';
import { BanIcon, CameraIcon, CheckCircle2Icon, CheckIcon, QrCodeIcon, ScanLineIcon, ShieldCheckIcon, XCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { CustodyCase } from '../../types/case';
import type { DemoUser } from '../../types/config';
import type { FieldScreen } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { ScannerView } from '../../components/mobile/ScannerView';
import { DOC_LABEL, PAYMENT_LABEL, categoryLabel } from '../../utils/caseStatus';
import { fmtDate, fmtDateTime, nowIso } from '../../utils/bn';
type Step = 'scan' | 'result' | 'verify' | 'handover' | 'success';
type Verdict = {
  ok: true;
  c: CustodyCase;
} | {
  ok: false;
  title: string;
  detail: string;
  c?: CustodyCase;
};
const CHECKS = [{
  id: 'barcode',
  label: 'যানবাহনের বারকোড PCR-এর সাথে মিলেছে'
}, {
  id: 'identity',
  label: 'যানবাহনের ভৌত পরিচয় (রং, ব্র্যান্ড, নম্বরপ্লেট) মিলেছে'
}, {
  id: 'claimant',
  label: 'মালিক / দাবিদারের পরিচয়পত্র যাচাই করা হয়েছে'
}, {
  id: 'authority',
  label: 'গ্রহণের কর্তৃত্ব (মালিক বা অনুমোদিত প্রতিনিধি) নিশ্চিত'
}, {
  id: 'location',
  label: 'পার্কিং অবস্থান থেকে যানবাহন শনাক্ত করা হয়েছে'
}, {
  id: 'status',
  label: 'অবমুক্তি অনুমোদনের অবস্থা বৈধ'
}];
const ID_TYPES = ['জাতীয় পরিচয়পত্র', 'পাসপোর্ট', 'ড্রাইভিং লাইসেন্স', 'জাতীয় পরিচয়পত্র + কর্তৃত্বপত্র'];
export function ReleaseFlow({
  user,
  go
}: {
  user: DemoUser;
  go: (s: FieldScreen) => void;
}) {
  const {
    cases,
    config,
    updateCase,
    getCase
  } = useStore();
  const [step, setStep] = useState<Step>('scan');
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [lastToken, setLastToken] = useState('');
  const [manual, setManual] = useState('');
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [barcodeMsg, setBarcodeMsg] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [rx, setRx] = useState({
    name: '',
    idType: ID_TYPES[0],
    mobile: '',
    ack: false,
    remarks: '',
    photo: false
  });
  const letters = cases.filter(c => c.letter && c.letter.status !== 'draft');
  const scan = (token: string) => {
    const t = token.trim().toUpperCase();
    setLastToken(t);
    const c = cases.find(x => x.letter?.qrToken === t);
    let v: Verdict;
    if (!c || !c.letter) v = {
      ok: false,
      title: 'অবৈধ QR',
      detail: 'এই QR কোডের সাথে কোনো বৈধ অবমুক্তি পত্র পাওয়া যায়নি। অবমুক্তি বন্ধ।'
    };else if (c.letter.status === 'redeemed') v = {
      ok: false,
      title: 'এই অবমুক্তি পত্র ইতোমধ্যে ব্যবহার করা হয়েছে।',
      detail: 'একই পত্র দিয়ে দ্বিতীয়বার অবমুক্তি সম্ভব নয়।',
      c
    };else if (c.letter.status === 'cancelled') v = {
      ok: false,
      title: 'অবমুক্তি পত্র বাতিলকৃত',
      detail: 'যথাযথ কর্তৃপক্ষ এই পত্র বাতিল করেছেন।',
      c
    };else if (c.approval === 'held') v = {
      ok: false,
      title: 'অনুমোদন স্থগিত',
      detail: 'নথি স্থগিত থাকায় অবমুক্তি বন্ধ।',
      c
    };else if (c.letter.status !== 'issued') v = {
      ok: false,
      title: 'পত্র এখনো ইস্যু হয়নি',
      detail: 'স্বাক্ষর ও ইস্যু সম্পন্ন না হওয়া পর্যন্ত QR বৈধ নয়।',
      c
    };else v = {
      ok: true,
      c
    };
    if (!v.ok && v.c) {
      updateCase(v.c.pcr, x => x, {
        key: 'blocked_scan',
        actor: user.name,
        role: user.title,
        action: `অবমুক্তি QR স্ক্যান প্রত্যাখ্যাত — ${v.title}`
      });
    }
    setVerdict(v);
    setChecks({});
    setBarcodeMsg(null);
    if (v.ok) setRx({
      name: v.c.ownerName,
      idType: ID_TYPES[0],
      mobile: v.c.mobile,
      ack: false,
      remarks: '',
      photo: false
    });
    setStep('result');
  };
  const live = verdict?.c ? getCase(verdict.c.pcr) : undefined;
  const allChecked = CHECKS.every(c => checks[c.id]);
  const scanVehicleBarcode = (match: boolean) => {
    if (!live) return;
    if (match) {
      setBarcodeMsg({
        ok: true,
        text: `${live.barcode.serial} — PCR ${live.pcr}-এর সাথে মিলেছে।`
      });
      setChecks(c => ({
        ...c,
        barcode: true
      }));
    } else {
      setBarcodeMsg({
        ok: false,
        text: 'স্ক্যানকৃত বারকোড STK-006790 এই PCR-এর নয়। ভিন্ন যানবাহন — হস্তান্তর করবেন না।'
      });
      setChecks(c => ({
        ...c,
        barcode: false
      }));
    }
  };
  const handover = () => {
    if (!live || !rx.name.trim() || !rx.ack) {
      toast.error('গ্রহণকারীর নাম ও গ্রহণের স্বীকৃতি আবশ্যক।');
      return;
    }
    const at = nowIso();
    updateCase(live.pcr, x => ({
      ...x,
      stage: 'released',
      letter: x.letter ? {
        ...x.letter,
        status: 'redeemed',
        redeemedAt: at,
        redeemedBy: user.name,
        receiverName: rx.name,
        receiverIdType: rx.idType,
        receiverMobile: rx.mobile
      } : x.letter
    }), {
      key: 'released',
      actor: user.name,
      role: user.title,
      action: 'যানবাহন অবমুক্ত ও হস্তান্তর সম্পন্ন — QR ব্যবহৃত',
      note: rx.remarks || undefined
    });
    setStep('success');
    toast.success('যানবাহন সফলভাবে অবমুক্ত ও হস্তান্তর করা হয়েছে।');
  };
  const title = {
    scan: 'অবমুক্তি পত্র যাচাই',
    result: 'অবমুক্তি অনুমোদনের ফলাফল',
    verify: 'ভৌত যাচাই',
    handover: 'চূড়ান্ত হস্তান্তর',
    success: 'হস্তান্তর সম্পন্ন'
  }[step];
  const back = () => setStep(step === 'handover' ? 'verify' : step === 'verify' ? 'result' : 'scan');
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" title={title} onBack={step === 'scan' || step === 'success' ? () => go({
      name: 'home'
    }) : back} data-id="element-1518" data-mp="1izf5948i79f9" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3" data-id="element-1519" data-mp="149pq253kt4vz">
        {step === 'scan' && <>
            <ScannerView mode="qr" hint="অবমুক্তি পত্রের QR কোড ফ্রেমে রাখুন" data-id="element-1520" data-mp="v9b0uunhr5n7" />
            <div className="card p-3" data-id="element-1521" data-mp="vbe2yd1x4p8a">
              <p className="text-sm font-bold text-ink" data-id="element-1522" data-mp="108xfn8iywty4">ডেমো QR স্ক্যান</p>
              <div className="mt-2 space-y-1.5" data-id="element-1523" data-mp="lb50wil8w9kz">
                {letters.map(c => <button key={c.pcr} onClick={() => scan(c.letter?.qrToken ?? '')} className="flex w-full items-center justify-between rounded-lg border border-line px-3 py-2 text-left hover:bg-navy-50" data-id="element-1524" data-mp="ldhrfk52js9n">
                    <span data-id="element-1525" data-mp="2fnbko5s7fyow">
                      <span className="block font-mono text-xs font-bold text-navy" data-id="element-1526" data-mp="2kor6w164cb8v">{c.letter?.qrToken}</span>
                      <span className="text-xs text-ink-muted" data-id="element-1527" data-mp="2j7c28zp8e9l1">{c.pcr} · {c.letter?.status === 'redeemed' ? 'ব্যবহৃত পত্র' : c.letter?.status === 'issued' ? 'ইস্যুকৃত পত্র' : 'অসম্পূর্ণ পত্র'}</span>
                    </span>
                    <QrCodeIcon className="h-4 w-4 text-navy" data-id="element-1528" data-mp="299efyhqxmmv2" />
                  </button>)}
                <button onClick={() => scan('RL9999-FAKE01')} className="flex w-full items-center justify-between rounded-lg border border-danger/30 px-3 py-2 text-left hover:bg-danger-50" data-id="element-1529" data-mp="2593z2a1lqc1f">
                  <span data-id="element-1530" data-mp="2wz0f557b1232"><span className="block font-mono text-xs font-bold text-danger" data-id="element-1531" data-mp="2wz8sszb94bfb">RL9999-FAKE01</span><span className="text-xs text-ink-muted" data-id="element-1532" data-mp="2s6fmi704wv14">জাল / অজানা QR (ব্যতিক্রম ডেমো)</span></span>
                  <QrCodeIcon className="h-4 w-4 text-danger" data-id="element-1533" data-mp="2o5nf7jatp07h" />
                </button>
              </div>
            </div>
            <div className="flex gap-2" data-id="element-1534" data-mp="3kq5vrml2jyef">
              <input className="input font-mono" placeholder="QR টোকেন লিখুন" value={manual} onChange={e => setManual(e.target.value)} data-id="element-1535" data-mp="3fwtanmflm9qf" />
              <button className="btn btn-primary" onClick={() => scan(manual)} data-id="element-1536" data-mp="12g9q9fbw4osx">যাচাই</button>
            </div>
          </>}

        {step === 'result' && verdict && (verdict.ok && live ? <>
              <div className="rounded-2xl bg-success p-5 text-center text-white" data-id="element-1537" data-mp="1bhput6g0xwp8">
                <ShieldCheckIcon className="mx-auto h-12 w-12" data-id="element-1538" data-mp="1gcdbwk8p7ots" />
                <p className="mt-2 text-xl font-bold" data-id="element-1539" data-mp="1lchssbdiqlkj">অবমুক্তির অনুমোদন বৈধ</p>
                <p className="text-xs text-white/85" data-id="element-1540" data-mp="1qc4bh0c48u0q">যাচাই: {fmtDateTime(nowIso())}</p>
              </div>
              <dl className="card divide-y divide-line text-sm" data-id="element-1541" data-mp="1v345nxtuoxzj">
                {[['অবমুক্তি পত্র নম্বর', live.letter?.number], ['PCR নম্বর', live.pcr], ['রেজিস্ট্রেশন নম্বর', live.regNo], ['যানবাহনের ধরন', categoryLabel(live.category, config)], ['মালিক / অনুমোদিত দাবিদার', live.ownerName], ['ডাম্পিং স্টেশন', `${live.station?.station ?? live.assignedStation} · ${live.station?.slot ?? ''}`], ['অনুমোদনের অবস্থা', 'অনুমোদিত ও স্বাক্ষরিত'], ['পত্র ইস্যুর তারিখ', fmtDate(live.letter?.issuedAt)], ['বারকোড রেফারেন্স', live.barcode.serial], ['পেমেন্ট ক্লিয়ারেন্স', PAYMENT_LABEL[live.paymentStatus].label], ['কাগজপত্র যাচাই', DOC_LABEL[live.docStatus].label], ['দাবিদার যাচাই', 'পরিচয়পত্র যাচাই আবশ্যক']].map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-4 py-2" data-id="element-1542" data-mp="w8kpuisqjeam"><dt className="text-ink-muted" data-id="element-1543" data-mp="1a3d9ntfnrgn0">{k}</dt><dd className="text-right font-semibold" data-id="element-1544" data-mp="15b3oeqphn4r2">{v}</dd></div>)}
              </dl>
            </> : <>
              <div className="rounded-2xl bg-danger p-5 text-center text-white" data-id="element-1545" data-mp="hk6l22brdfrf">
                {verdict.ok ? null : verdict.c?.letter?.status === 'redeemed' ? <BanIcon className="mx-auto h-12 w-12" data-id="element-1546" data-mp="3fzboxhdb4fe" /> : <XCircleIcon className="mx-auto h-12 w-12" data-id="element-1547" data-mp="3exv6dhl1pqa" />}
                <p className="mt-2 text-lg font-bold leading-snug" data-id="element-1548" data-mp="86fnkd5jihzn">{!verdict.ok && verdict.title}</p>
                <p className="mt-1 text-xs text-white/85" data-id="element-1549" data-mp="3pnfmx7pztgd4">{!verdict.ok && verdict.detail}</p>
              </div>
              <p className="rounded-lg bg-danger-50 p-3 text-xs font-semibold text-danger-700" data-id="element-1550" data-mp="3ulqypnzum0xz">অবমুক্তি বন্ধ — এই প্রচেষ্টা অডিট লগে সংরক্ষিত হয়েছে। QR: <span className="font-mono" data-id="element-1551" data-mp="m705z87jmbsm">{lastToken}</span></p>
              {!verdict.ok && verdict.c?.letter?.status === 'redeemed' && <dl className="card divide-y divide-line text-sm" data-id="element-1552" data-mp="c8awo3tppznz">
                  <p className="px-4 py-2 text-xs font-bold text-ink" data-id="element-1553" data-mp="c814qap3xifn">পূর্ববর্তী হস্তান্তরের তথ্য</p>
                  {[['PCR', verdict.c.pcr], ['পত্র নম্বর', verdict.c.letter?.number], ['হস্তান্তরের সময়', fmtDateTime(verdict.c.letter?.redeemedAt)], ['গ্রহণকারী', verdict.c.letter?.receiverName], ['পরিচয় যাচাই', verdict.c.letter?.receiverIdType], ['হস্তান্তরকারী কর্মকর্তা', verdict.c.letter?.redeemedBy]].map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-4 py-2" data-id="element-1554" data-mp="22oi5sc7ogt1d"><dt className="text-ink-muted" data-id="element-1555" data-mp="27o34clixqzdk">{k}</dt><dd className="text-right font-semibold" data-id="element-1556" data-mp="27n1toi5u0ewd">{v ?? '—'}</dd></div>)}
                </dl>}
            </>)}

        {step === 'verify' && live && <>
            <div className="card p-3" data-id="element-1557" data-mp="1yllhq5zdc4q1">
              <p className="text-sm font-bold text-ink" data-id="element-1558" data-mp="1jqxg52x39qfp">যানবাহনের বারকোড মিলিয়ে দেখুন</p>
              <p className="text-xs text-ink-muted" data-id="element-1559" data-mp="1op8otabnduwc">প্রত্যাশিত: <span className="font-mono font-semibold" data-id="element-1560" data-mp="1oq0lkogzdyo7">{live.barcode.serial}</span> · স্লট {live.station?.slot}</p>
              <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-1561" data-mp="3e587pj52iyp6">
                <button className="btn btn-sm btn-primary" onClick={() => scanVehicleBarcode(true)} data-id="element-1562" data-mp="3j2gopgehcwou"><ScanLineIcon className="h-3.5 w-3.5" data-id="element-1563" data-mp="17fmz612d0p6y" /> বারকোড স্ক্যান</button>
                <button className="btn btn-sm btn-outline" onClick={() => scanVehicleBarcode(false)} data-id="element-1564" data-mp="12h3ctzjutbe2">ভিন্ন বারকোড (ডেমো)</button>
              </div>
              {barcodeMsg && <p className={`mt-2 rounded-md p-2 text-xs ${barcodeMsg.ok ? 'bg-success-50 text-success' : 'bg-danger-50 text-danger-700'}`} data-id="element-1565" data-mp="1h7aj7gwl73ye">{barcodeMsg.text}</p>}
            </div>
            <div className="card divide-y divide-line" data-id="element-1566" data-mp="1q5cr8geqmwv6">
              {CHECKS.map(ch => <label key={ch.id} className="flex items-start gap-3 px-4 py-3 text-sm" data-id="element-1567" data-mp="1m410g7k4o5le">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 accent-bdgreen" disabled={ch.id === 'barcode'} checked={!!checks[ch.id]} onChange={e => setChecks(c => ({
              ...c,
              [ch.id]: e.target.checked
            }))} data-id="element-1568" data-mp="2053o2oqrtnyf" />
                  <span className={checks[ch.id] ? 'text-ink' : 'text-ink-muted'} data-id="element-1569" data-mp="5rlxesv2kaz6">{ch.label}</span>
                </label>)}
            </div>
          </>}

        {step === 'handover' && live && <>
            <div data-id="element-1570" data-mp="20wbt5glb40q6"><label className="label" htmlFor="rxn" data-id="element-1571" data-mp="20wbubjx1m6rr">গ্রহণকারীর নাম</label><input id="rxn" className="input" value={rx.name} onChange={e => setRx({
            ...rx,
            name: e.target.value
          })} data-id="element-1572" data-mp="25tv7shlx2fin" /></div>
            <div data-id="element-1573" data-mp="29wrwmq21v7v1"><label className="label" htmlFor="rxi" data-id="element-1574" data-mp="29x08cemfs74n">পরিচয় যাচাইয়ের ধরন</label><select id="rxi" className="input" value={rx.idType} onChange={e => setRx({
            ...rx,
            idType: e.target.value
          })} data-id="element-1575" data-mp="2eujl1a3hkbuh">{ID_TYPES.map(t => <option key={t} data-id="element-1576" data-mp="2jr0l92nymgnx">{t}</option>)}</select></div>
            <div data-id="element-1577" data-mp="2oqwgodilczrv"><label className="label" htmlFor="rxm" data-id="element-1578" data-mp="2oqmy3azvluw7">মোবাইল নম্বর</label><input id="rxm" className="input" value={rx.mobile} onChange={e => setRx({
            ...rx,
            mobile: e.target.value
          })} data-id="element-1579" data-mp="2tiwkwi5p2eu9" /></div>
            <div data-id="element-1580" data-mp="2xj6qhtpkr38l"><label className="label" data-id="element-1581" data-mp="2xj6rnx1b99a6">হস্তান্তরের তারিখ ও সময়</label><div className="input flex items-center" data-id="element-1582" data-mp="32jcnlpem5hy2">{fmtDateTime(nowIso())}</div></div>
            <div data-id="element-1583" data-mp="32hhzy3byjbqa"><label className="label" htmlFor="rxr" data-id="element-1584" data-mp="37iffe85l1ro3">কর্মকর্তার মন্তব্য</label><textarea id="rxr" className="textarea" rows={2} value={rx.remarks} onChange={e => setRx({
            ...rx,
            remarks: e.target.value
          })} data-id="element-1585" data-mp="37ixinoqnx65i" /></div>
            <div data-id="element-1586" data-mp="3cnj6uccjltn7">
              <label className="label" data-id="element-1587" data-mp="3cn007rsceroh">হস্তান্তরের ছবি (ঐচ্ছিক)</label>
              <button onClick={() => setRx({
            ...rx,
            photo: !rx.photo
          })} className="relative flex h-24 w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-line bg-canvas text-xs text-ink-muted" data-id="element-1588" data-mp="37o6p260j3crh">
                {rx.photo ? <img src={live.photo} alt="হস্তান্তরের ছবি" className="absolute inset-0 h-full w-full object-cover" data-id="element-1589" data-mp="464yifna7dbz" /> : <span className="flex flex-col items-center" data-id="element-1590" data-mp="3veapy5mc0yes"><CameraIcon className="mb-1 h-5 w-5" data-id="element-1591" data-mp="3qn10b50s9v2w" />ছবি তুলুন (সিমুলেটেড)</span>}
              </button>
            </div>
            <label className="flex items-start gap-2.5 rounded-lg border border-line bg-white p-3 text-sm" data-id="element-1592" data-mp="3mlyxyfbkl2wp">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-bdgreen" checked={rx.ack} onChange={e => setRx({
            ...rx,
            ack: e.target.checked
          })} data-id="element-1593" data-mp="my7l07mltnu5" />
              <span data-id="element-1594" data-mp="i5ebz7k3rtc9">গ্রহণের স্বীকৃতি: আমি যানবাহনটি বর্তমান অবস্থায় বুঝে পেয়েছি।</span>
            </label>
          </>}

        {step === 'success' && live && <div className="flex flex-col items-center pt-6 text-center" data-id="element-1595" data-mp="7plx2hm7jdm6">
            <CheckCircle2Icon className="h-16 w-16 text-success" data-id="element-1596" data-mp="3puyw210cagxk" />
            <p className="mt-3 text-lg font-bold text-ink" data-id="element-1597" data-mp="3tv91nck7z5bw">যানবাহন সফলভাবে অবমুক্ত ও হস্তান্তর করা হয়েছে।</p>
            <p className="text-sm text-ink-muted" data-id="element-1598" data-mp="lf79181laskp">{live.pcr} · {live.regNo}</p>
            <div className="mt-4 w-full rounded-xl bg-canvas p-3 text-left text-xs space-y-1" data-id="element-1599" data-mp="qesiicedyjbo">
              <p data-id="element-1600" data-mp="cndnt8b48tos">কেসের অবস্থা: <b data-id="element-1601" data-mp="cnncmskfcusw">অবমুক্ত ও হস্তান্তর সম্পন্ন</b></p>
              <p data-id="element-1602" data-mp="cn5a5e765kcj">QR অবস্থা: <b data-id="element-1603" data-mp="claykqhsf4l4">ব্যবহৃত (পুনরায় ব্যবহারযোগ্য নয়)</b></p>
              <p data-id="element-1604" data-mp="gnxlqe3ik1bz">সক্রিয় স্টেশন ইনভেন্টরি থেকে অপসারিত</p>
            </div>
          </div>}
      </div>

      <div className="shrink-0 border-t border-line bg-white p-3 flex gap-2" data-id="element-1605" data-mp="14dry6ujx7nvp">
        {step === 'result' && (verdict?.ok ? <button className="btn btn-green btn-lg w-full" onClick={() => setStep('verify')} data-id="element-1606" data-mp="197ea2ki9fnqg">ভৌত যাচাই শুরু করুন</button> : <button className="btn btn-outline btn-lg w-full" onClick={() => setStep('scan')} data-id="element-1607" data-mp="2owlwjw3olo7q">পুনরায় স্ক্যান</button>)}
        {step === 'verify' && <button className="btn btn-green btn-lg w-full" disabled={!allChecked} onClick={() => setStep('handover')} data-id="element-1608" data-mp="33kgrzk92q6oi"><CheckIcon className="h-5 w-5" data-id="element-1609" data-mp="2ynqcoz53ff45" /> যাচাই সম্পন্ন — হস্তান্তরে যান</button>}
        {step === 'handover' && <button className="btn btn-green btn-lg w-full" onClick={handover} data-id="element-1610" data-mp="2axmc70n7bvhd">যানবাহন হস্তান্তর নিশ্চিত করুন</button>}
        {step === 'success' && <>
            <button className="btn btn-outline flex-1" onClick={() => scan(lastToken)} data-id="element-1611" data-mp="2kvjwxe5ura58">একই QR পুনরায় স্ক্যান</button>
            <button className="btn btn-primary flex-1" onClick={() => setStep('scan')} data-id="element-1612" data-mp="2fuw54q9sup9y">নতুন স্ক্যান</button>
          </>}
      </div>
    </>;
}